/**
 * InvitationAcceptPage - Handle invitation acceptance for admin roles
 * Rendered at /join/i_:invitationId (UnifiedJoinPage). Legacy /auth/accept-invitation?id= redirects to /join/i_:id.
 *
 * @param {string} [invitationId] - Optional; when provided (e.g. from UnifiedJoinPage), used instead of query param.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';
import { db } from '@healthdesk/shared-services';
import {
  Box,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  Button,
} from '@mui/material';
import { contentCardSx } from '../../styles/standaloneLayoutTokens';
import { CheckCircleRounded, ErrorRounded } from '@mui/icons-material';
import PhoneAuthFlow from 'components/auth/PhoneAuthFlow';

const InvitationAcceptPage = ({ invitationId: invitationIdProp }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const invitationIdFromQuery = searchParams.get('id');
  const invitationId = invitationIdProp ?? invitationIdFromQuery;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [invitation, setInvitation] = useState(null);
  const [accepted, setAccepted] = useState(false);

  useEffect(() => {
    loadInvitation();
  }, [invitationId]);

  const loadInvitation = async () => {
    if (!invitationId) {
      setError('Invalid invitation link. Please request a new invitation.');
      setLoading(false);
      return;
    }

    try {
      const invitationDoc = await db.collection('invitations').doc(invitationId).get();

      if (!invitationDoc.exists) {
        setError('Invitation not found. It may have been cancelled or expired.');
        setLoading(false);
        return;
      }

      const invitationData = invitationDoc.data();

      // Check if invitation is expired
      if (invitationData.expiresAt && invitationData.expiresAt.toDate() < new Date()) {
        setError('This invitation has expired. Please request a new invitation.');
        setLoading(false);
        return;
      }

      // Check if invitation was already accepted
      if (invitationData.status === 'accepted') {
        setError('This invitation has already been accepted. Please log in to your account.');
        setLoading(false);
        return;
      }

      setInvitation({
        id: invitationDoc.id,
        ...invitationData,
      });
      setLoading(false);
    } catch (err) {
      console.error('Error loading invitation:', err);
      setError('Error loading invitation. Please try again.');
      setLoading(false);
    }
  };

  const handleAuthSuccess = async (user) => {
    if (!invitation) return;

    try {
      // Accept invitation server-side (Cloud Function validates email match and writes systemGroupRoles)
      const acceptInvitation = firebase.functions().httpsCallable('acceptInvitation');
      await acceptInvitation({ invitationId: invitation.id });

      setAccepted(true);

      // Redirect to dashboard after a brief delay
      setTimeout(() => {
        navigate('/dashboard');
      }, 2000);
    } catch (err) {
      console.error('Error completing invitation acceptance:', err);
      setError(
        err?.details ?? err?.message ?? 'Error setting up your account. Please contact support.'
      );
    }
  };

  const getRoleDisplayName = (role) => {
    const roleNames = {
      'chartmind-admin': 'ChartMind Administrator',
      'chartmind-kijabe': 'ChartMind Kijabe Administrator',
      'chartmind-supervisor': 'Clinic Manager',
      'system-medical-admin': 'System Medical Administrator',
      'advertiser': 'Promotional Sponsor',
    };
    return roleNames[role] || role;
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.default',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.default',
          p: 3,
        }}
      >
        <Paper elevation={0} sx={{ ...contentCardSx, p: 4, maxWidth: 500, textAlign: 'center' }}>
          <ErrorRounded sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            Invitation Error
          </Typography>
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
          <Button variant="contained" onClick={() => navigate('/auth')}>
            Go to Login
          </Button>
        </Paper>
      </Box>
    );
  }

  if (accepted) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.default',
          p: 3,
        }}
      >
        <Paper elevation={0} sx={{ ...contentCardSx, p: 4, maxWidth: 500, textAlign: 'center' }}>
          <CheckCircleRounded sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            Welcome!
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            Your account has been set up as a {getRoleDisplayName(invitation?.role)}.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Redirecting to your dashboard...
          </Typography>
          <CircularProgress size={24} sx={{ mt: 2 }} />
        </Paper>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
        p: 3,
      }}
    >
      <Paper elevation={0} sx={{ ...contentCardSx, p: 4, maxWidth: 500, width: '100%' }}>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            You're Invited!
          </Typography>
          <Typography color="text.secondary">
            You've been invited to join HealthDesk as a
          </Typography>
          <Typography variant="h6" color="primary" sx={{ mt: 1 }}>
            {getRoleDisplayName(invitation?.role)}
          </Typography>
        </Box>

        <Alert severity="info" sx={{ mb: 3 }}>
          Complete your account setup by verifying your phone number.
        </Alert>

        <PhoneAuthFlow
          onSuccess={handleAuthSuccess}
          preselectedRole={invitation?.role}
          inDialog={false}
        />
      </Paper>
    </Box>
  );
};

export default InvitationAcceptPage;
